import { IntlShape, MessageDescriptor } from 'react-intl';

import {
  humanizeKey,
  signatureRoleSuffix,
} from 'containers/OfferLetterTemplates/constants';

import { serverMissingFields } from './errors';
import messages from './messages';
import { MissingField } from './types';

/**
 * Splitting a failed readiness result into the two kinds of block it can carry
 * (AXHE-4441).
 *
 * Every entry in `missing_fields` used to be treated as a field the recruiter
 * had left empty: it flagged a control, and it counted towards "N fields still
 * need a value". One reason cannot be either of those things.
 * `unresolvable_signer` is a `[SIGNATURE_<ROLE>]` placeholder for a role that
 * isn't on the template's signer roster, and this form deliberately renders no
 * control for a signature field (`OfferLetters::OfferLetterForm#form_fields`).
 * So the recruiter was shown a count of fields needing values, with every field
 * on the panel filled in and nothing marked — a blocked send with nothing to
 * point at, and no value they could type that would clear it.
 *
 * Both halves of that were individually right: a dangling tag must block the
 * send, and a signature placeholder is not a recruiter input. What was missing
 * is that they are different KINDS of problem and belong on different surfaces.
 * The reason has always been on the wire; this is the client finally reading it.
 *
 * `can_generate` is untouched by any of this — the send stays blocked either
 * way. This changes only what the recruiter is told.
 */

/**
 * A blocked send with no field behind it, fixed on the template:
 *
 * - `unresolvable_signer`: a signature placeholder naming a role the template
 *   has no signer for.
 * - `unplaced_signer`: a signer the template has, with no signature placeholder
 *   this offer prints. `key` is the placeholder to add.
 */
export interface SignerBlock {
  /** The placeholder's field key, e.g. `SIGNATURE_HIRING_MANAGER`. */
  key: string;
  /**
   * The roster role key it names, e.g. `hiring_manager` — humanize for display,
   * the same way a field with no authored label is named.
   */
  role: string;
  reason: 'unresolvable_signer' | 'unplaced_signer';
}

/**
 * The minimum both readers need. Deliberately NOT `PreviewResult`: the create
 * and update endpoints refuse with the SAME per-field problems in a 422 body
 * (`{ message, missing_fields }`, `offer_letters_controller.rb:64`), and that
 * body carries no `can_generate` or `approval`. Typing this structurally is what
 * lets one splitter serve both surfaces — the readiness check that runs before
 * the CTA, and the refusal that comes back when the form was never shown at all
 * (AXHE-4441 review).
 */
export interface ReadinessProblems {
  missing_fields?: MissingField[];
}

const missingFields = (result?: ReadinessProblems): MissingField[] =>
  result?.missing_fields ?? [];

const isSignerBlock = (missing: MissingField): boolean =>
  missing.reason === 'unresolvable_signer' || missing.reason === 'unplaced_signer';

/**
 * The entries that DO name a field on this panel — what drives the per-field
 * errors and the missing-field count.
 *
 * A key here can still be absent from the DOM (the client hides a field the
 * server asked for, one keystroke out of step), which is a separate question
 * handled where the reveal looks it up.
 */
export const fieldProblems = (result?: ReadinessProblems): MissingField[] =>
  missingFields(result).filter(missing => !isSignerBlock(missing));

/**
 * The signer roles blocking the send, in the order the server reported them.
 *
 * `role` falls back to the placeholder's own key when the key carries no `_`
 * suffix. That is unreachable through the server — a bare `[SIGNATURE]` always
 * resolves to the primary signer, so it is never flagged — and the fallback is
 * there only so a future server bug surfaces as an awkwardly-worded block rather
 * than as no block at all. Silence in front of a disabled send is the exact
 * failure this file exists to remove.
 */
export const signerBlocks = (result?: ReadinessProblems): SignerBlock[] =>
  missingFields(result)
    .filter(isSignerBlock)
    .map(missing => ({
      key: missing.key,
      role: signatureRoleSuffix(missing.key) ?? missing.key,
      reason: missing.reason as SignerBlock['reason'],
    }));

/**
 * The sentence a signer block reads as, for `FormattedMessage` or
 * `intl.formatMessage`. A bare `SIGNATURE` is the applicant's placeholder, so
 * an unplaced one names the applicant rather than a role.
 */
export const signerBlockMessage = (
  block: SignerBlock,
): { message: MessageDescriptor; values: Record<string, string> } => {
  if (block.reason === 'unresolvable_signer') {
    return {
      message: messages.signerNotOnTemplate,
      values: { role: humanizeKey(block.role) },
    };
  }

  const placeholder = `[${block.key}]`;
  if (signatureRoleSuffix(block.key) === null) {
    return { message: messages.applicantSignatureNotPlaced, values: { placeholder } };
  }

  return {
    message: messages.signerSignatureNotPlaced,
    values: { placeholder, role: humanizeKey(block.role) },
  };
};

/**
 * The sentences a refused create, update, revision or approval-queue edit reads
 * as when its 422 names signer blocks, or `undefined` when it names none and
 * the caller's own copy applies. The server's `message` names a reason token
 * and a merge key, which is never copy for a recruiter.
 *
 * No next step follows them. An existing offer renders from the frozen copy of
 * its template (`OfferLetters::ResolveRenderTemplate`), so what clears the
 * refusal depends on the offer: a draft rebuilds from the live template when it
 * is reopened, an unsent revision is discarded, a sent offer is canceled and
 * sent again, and an offer older than the frozen copy retries as is. Only an
 * offer rendered before this check can carry a frozen copy that fails it.
 */
export const signerRefusal = (intl: IntlShape, error: unknown): string | undefined => {
  const blocks = signerBlocks({ missing_fields: serverMissingFields(error) });
  if (blocks.length === 0) return undefined;

  return blocks
    .map(block => {
      const { message, values } = signerBlockMessage(block);
      return intl.formatMessage(message, values);
    })
    .join(' ');
};
